import React from 'react';
import { ShieldCheck, CheckCircle2, Cloud, Sparkles, ExternalLink, Lock } from 'lucide-react';

interface GoogleCloudPartnerBadgeProps {
  variant?: 'compact' | 'full' | 'banner' | 'pill';
  className?: string;
}

export function GoogleCloudPartnerBadge({ variant = 'compact', className = '' }: GoogleCloudPartnerBadgeProps) {
  if (variant === 'pill') {
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#121516] border border-[#2A2F31] shadow-xs text-xs ${className}`}>
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
            <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" fill="#4285F4"/>
            <path d="M19 14h-1.5c0-2.76-2.24-5-5-5-1.22 0-2.33.44-3.2 1.17l-1.07-1.07C9.37 8.36 10.62 8 12 8c3.31 0 6 2.69 6 6z" fill="#34A853"/>
            <path d="M7 14c0-1.66 1.34-3 3-3 .78 0 1.48.3 2.02.79l1.07-1.07C12.28 9.94 11.2 9.5 10 9.5c-2.48 0-4.5 2.02-4.5 4.5H7z" fill="#FBBC05"/>
            <path d="M19 19H6c-2.76 0-5-2.24-5-5 0-2.48 1.81-4.54 4.21-4.93l1.14 1.14C4.88 10.67 4 12.22 4 14c0 2.21 1.79 4 4 4h11c2.21 0 4-1.79 4-4 0-1.89-.98-3.53-2.45-4.29l1.14-1.14C23.16 9.57 24 11.66 24 14c0 2.76-2.24 5-5 5z" fill="#EA4335"/>
          </svg>
          <span className="font-bold text-gray-100">Google Cloud</span>
          <span className="text-[#14B8A6] font-semibold">Partner Network</span>
        </div>
        <span className="text-gray-600 hidden sm:inline">•</span>
        <span className="text-[11px] font-mono text-emerald-400 flex items-center gap-1">
          <CheckCircle2 size={11} /> Member (azuraos.app)
        </span>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className={`p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-[#111A24] via-[#12161A] to-[#0E1517] border border-sky-500/30 shadow-lg text-left relative overflow-hidden ${className}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-400 shrink-0 shadow-inner">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" fill="#4285F4"/>
              </svg>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-sky-400 uppercase tracking-wider font-bold">
                  Official Ecosystem Recognition
                </span>
                <span className="px-2 py-0.5 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 rounded border border-emerald-500/30 flex items-center gap-1">
                  <CheckCircle2 size={10} /> Verified Member
                </span>
                <span className="px-2 py-0.5 text-[10px] font-extrabold uppercase bg-purple-500/20 text-purple-300 rounded border border-purple-500/30">
                  Women in Tech ZA Member
                </span>
              </div>
              <h4 className="text-base font-bold text-white tracking-tight">
                Google Cloud Partner Network &amp; Google for Startups Cloud Program — Start Tier
              </h4>
              <p className="text-xs text-gray-300 leading-relaxed max-w-2xl">
                AzuraOS is built by <strong className="text-white">Azura Seraph™ (Pty) Ltd</strong> in South Africa (Reg: 2024/560516/07) and powered by enterprise Google Cloud infrastructure &amp; Google AI Studio, backed by official Google Cloud Partner Network membership and the Google for Startups Cloud Program.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-start sm:items-end gap-1.5 shrink-0 font-mono text-xs">
            <span className="text-gray-400 text-[11px]">Domain: <span className="text-white font-bold">azuraos.app</span></span>
            <span className="text-emerald-400 text-[11px] flex items-center gap-1">
              <ShieldCheck size={12} /> Enterprise Cloud SLA
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'full') {
    return (
      <div className={`bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl p-5 sm:p-6 space-y-4 text-white shadow-xl ${className}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#2A2F31] pb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center shrink-0">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" fill="#4285F4"/>
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded bg-sky-500/15 border border-sky-500/30 text-sky-300 text-[10px] font-mono font-bold uppercase tracking-wider">
                  Partner Network
                </span>
                <span className="px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono font-bold uppercase tracking-wider flex items-center gap-1">
                  <CheckCircle2 size={11} /> Verified Domain
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-bold font-display tracking-tight text-white mt-1">
                Google Cloud Partner Network Member
              </h3>
            </div>
          </div>
          <div className="font-mono text-xs text-gray-400 bg-[#151819] px-3 py-1.5 rounded-xl border border-[#2A2F31]">
            Domain: <strong className="text-teal-300">azuraos.app</strong>
          </div>
        </div>

        <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
          AzuraOS is built by <strong>Azura Seraph™ (Pty) Ltd</strong> (Registration Number: <strong>2024/560516/07</strong>, South Africa) and operates on institutional-tier Google Cloud architecture with direct membership in the <strong>Google Cloud Partner Network</strong>, as a <strong>Start Tier Recipient</strong> of the <strong>Google for Startups Cloud Program</strong>, and as a member of <strong>Women in Tech South Africa</strong>.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
          <div className="p-3.5 bg-[#151819] border border-[#2A2F31] rounded-xl space-y-1.5">
            <div className="flex items-center gap-2 text-sky-400 text-xs font-bold font-mono">
              <Cloud size={14} />
              <span>Google Cloud Platform</span>
            </div>
            <p className="text-[11px] text-gray-400 leading-normal">
              High-availability hosting, Google Cloud Run microservices, and global latency edge acceleration.
            </p>
          </div>

          <div className="p-3.5 bg-[#151819] border border-[#2A2F31] rounded-xl space-y-1.5">
            <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold font-mono">
              <ShieldCheck size={14} />
              <span>Zero-Trust Security</span>
            </div>
            <p className="text-[11px] text-gray-400 leading-normal">
              Firebase Firestore granular ABAC rules combined with client-side Web Crypto AES-256 isolation.
            </p>
          </div>

          <div className="p-3.5 bg-[#151819] border border-[#2A2F31] rounded-xl space-y-1.5">
            <div className="flex items-center gap-2 text-purple-400 text-xs font-bold font-mono">
              <Sparkles size={14} />
              <span>Google GenAI SDK</span>
            </div>
            <p className="text-[11px] text-gray-400 leading-normal">
              Direct Gemini API integration with zero middleman API markups and sovereign client token handling.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Compact default
  return (
    <div className={`p-3.5 sm:p-4 rounded-xl bg-[#121516] border border-[#2A2F31] hover:border-sky-500/40 transition-colors shadow-inner text-left ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-sky-500/10 border border-sky-500/30 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
              <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" fill="#4285F4"/>
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-xs text-white">Google Cloud Partner Network</span>
              <span className="px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 text-[9px] font-mono font-extrabold uppercase">
                MEMBER
              </span>
            </div>
            <p className="text-[11px] text-gray-400 font-mono">
              Official Partner • Domain: azuraos.app
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1 text-[11px] font-mono text-teal-400">
          <CheckCircle2 size={12} /> Verified
        </div>
      </div>
    </div>
  );
}
